<template>
  <v-card class="enode-card">

    <v-card-title class="pa-0 enode-card-header text-no-wrap">
      <car-brand-icon :carBrand="connectionDetails.brand"/>
      <div class="pt-5 pb-6 line-height-02"
           :class="showSameMultipleVehicles ? 'text-information-more-space' : 'text-information'">
        <span class="font-weight-bold font-size-04 mr-4" v-html="title"/><br>
        <span class="font-size-02">{{ $t('enode-connection-tile.range') }} {{
            connectionDetails.range
          }}{{ $t('app.unit.km') }}</span>
      </div>

      <v-tooltip v-show="showSameMultipleVehicles" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
              class="material-icons-outlined"
              v-show="showSameMultipleVehicles"
              v-bind="attrs"
              v-on="on" color="primary">
            info
          </v-icon>
        </template>
        <span>{{ $t('enode-connection-tile.multiple-cars') }}</span>
      </v-tooltip>

      <!-- Vehicle connection state icons -->
      <vehicle-connection-state-icons
        :is-at-charging-location="connectionDetails.locationId !== null"
        :is-reachable="connectionDetails.reachable"
        :is-plugged-in="connectionDetails.pluggedIn"
        :is-charging="connectionDetails.charging"
        :is-fully-charged="connectionDetails.fullyCharged"
        :is-smart-charging-paused="isSmartChargingPaused"
        :is-smart-charging-overriden="isSmartChargingOverriden"
        :is-charge-state-transitioning="isChargeStateTransitioning"
        :connection-information-background="connectionInformationBackground"
      />

    </v-card-title>

    <v-card-text>

      <div class="additional-battery-information mb-1">
        <span class="battery-capacity">{{ connectionDetails.batteryCapacity }} {{ $t('app.unit.kwh') }}</span>
      </div>

      <v-progress-linear v-if="connectionDetails.batteryLevel"
                         :color="progressBarColor"
                         :value="connectionDetails.batteryLevel"
                         class="mb-0 charging-progress-bar"
                         :buffer-value="progressVarBufferValue"
                         :stream="connectionDetails.charging"/>


      <div class="additional-charging-information mt-1">
        <span v-if="connectionDetails.batteryLevel" class="battery-level">{{ connectionDetails.batteryLevel }}%</span>
        <span v-else class="battery-level">N/A</span>
        <span v-if="connectionDetails.charging" class="charge-time-remaining">{{ timeRemaining }}</span>
      </div>

      <enode-advanced-settings :vehicle-id="connectionDetails.id"
                               :is-smart-charging-capable="connectionDetails.smartChargingCapable"/>

    </v-card-text>

    <v-card-actions>
      <v-btn block large depressed
             color="primary"
             class="font-weight-bold action-button"
             @click="requestCharging(connectionDetails)"
             :disabled="triggerStateChange || !connectionDetails.reachable || !connectionDetails.pluggedIn || connectionDetails.fullyCharged">
        <v-progress-circular
            v-if="triggerStateChange"
            indeterminate
            color="grey lighten-5"
        ></v-progress-circular>
        <span v-else-if="connectionDetails.hasSmartChargingPlan && !connectionDetails.charging">
          <v-icon small left>dynamic_form</v-icon>
          {{ $t('enode-connection-tile.start-charging') }}
        </span>
        <span v-else-if="isSmartChargingOverriden">
          <v-icon small left>undo</v-icon>
          {{ $t('enode-connection-tile.end-smart-override') }}
        </span>
        <span v-else-if="connectionDetails.charging">
          <v-icon small left>pause</v-icon>
          {{ $t('enode-connection-tile.stop-charging') }}
        </span>
        <span v-else>
          <v-icon small left>play_circle</v-icon>
          {{ $t('enode-connection-tile.start-charging') }}
        </span>
      </v-btn>
    </v-card-actions>

  </v-card>
</template>

<script>
import CarBrandIcon from "@/templates/components/CarBrandIcon"
import EnodeAdvancedSettings from "@/templates/components/emobility/EnodeAdvancedSettings.vue"
import VehicleConnectionStateIcons from "@/templates/components/homeEnergy/VehicleConnectionStateIcons"
import {SmartChargingStates, ChargeActions, ChargeActionStates} from "@/scripts/enodeConstants"

export default {
  name: "EnodeConnectionCard",
  components: {EnodeAdvancedSettings, CarBrandIcon, VehicleConnectionStateIcons},
  props: ['connectionDetails'],

  data() {
    return {
      greenCharging: false,
      timeBasedCharging: false,
      triggerStateChange: false,
      timeStamp: null,
      connectionInformationBackground: this.$vuetify.theme.currentTheme.primary + '33',
      amountOfSameVehicles: 0,
      actionState: null
    }
  },
  methods: {
    requestCharging(connectionDetails) {
      const {charging, smartChargingState, hasSmartChargingPlan} = connectionDetails
      const action = charging ? ChargeActions.STOP : ChargeActions.START

      this.triggerStateChange = true

      // Determine the correct API call configuration based on conditions
      if (smartChargingState === SmartChargingStates.OVERRIDDEN && action === ChargeActions.STOP) {
        // Case 1: Stop an active smart charging override
        this.$rhRequest.sendPost({
              endpoint: 'enode/end-smart-override',
              data: {
                vehicleId: connectionDetails.id
              }
            }, () => {
              setTimeout(() => {
                this.$emit('reloadConnectionDetails')
                this.triggerStateChange = false
              }, 10000)
            },
            (err) => {
              this.$root.bisatoast.error({message: this.$t('app.generic-error')})
              this.triggerStateChange = false
              console.error(err)
            }
        )
      } else if (hasSmartChargingPlan && action === ChargeActions.START) {
        // Case 2: Start charging immediately, overriding the smart plan
        this.$rhRequest.sendPost({
              endpoint: 'enode/create-smart-override',
              data: {
                vehicleId: connectionDetails.id
              }
            }, () => {
              setTimeout(() => {
                this.$emit('reloadConnectionDetails')
                this.triggerStateChange = false
              }, 10000)
            },
            (err) => {
              this.$root.bisatoast.error({message: this.$t('app.generic-error')})
              this.triggerStateChange = false
              console.error(err)
            }
        )
      } else {
        // Case 3: Standard start/stop charging request
        this.$rhRequest.sendPost({
              endpoint: 'enode/charge',
              data: {
                vehicleId: connectionDetails.id,
                action: action
              }
            }, (resp) => {
              this.checkChargingState(resp?.data?.data?.id)
            },
            (err) => {
              this.$root.bisatoast.error({message: this.$t('app.generic-error')})
              this.triggerStateChange = false
              console.error(err)
            }
        )
      }
    },
    checkChargingState(id) {
      this.timeStamp = this.timeStamp ?? Date.now()

      this.$rhRequest.sendGet({
        endpoint: 'enode/get-charge-action',
        params: {
          actionId: id
        }
      }, (resp) => {
        let successMsg = resp?.data?.data?.action === 'START' ? 'enode.success.start-charging' : 'enode.success.stop-charging'
        this.actionState = resp?.data?.data?.state
        this.requestedAction = resp?.data?.action

        switch (resp?.data?.data?.state) {
          case ChargeActionStates.PENDING:
            if ((Date.now() - this.timeStamp) < 60000) {
              setTimeout(() => {
                this.checkChargingState(id)
              }, 2000)
            } else {
              this.triggerStateChange = false
              this.$root.bisatoast.error({message: this.$t('app.generic-error')})
            }
            break
          case ChargeActionStates.FAILED:
          case ChargeActionStates.CANCELLED:
            this.triggerStateChange = false
            this.timeStamp = null
            this.$root.bisatoast.error({message: this.$t('app.generic-error')})
            break
          default:
            this.$root.bisatoast.success({message: this.$t(successMsg)})
            this.$emit('reloadConnectionDetails')
            this.triggerStateChange = false
            this.timeStamp = null
        }
      }, (err) => {
        this.timeStamp = null
        this.$root.bisatoast.error({message: this.$t('app.generic-error')})
        console.error(err)
      })
    },
    getAmountOfVehiclesWithSameVin() {
      this.$rhRequest.sendGet({
        endpoint: 'enode/get-amount-of-vehicles-with-same-vin?id=' + this.connectionDetails.id,
      }, (resp) => {
        this.amountOfSameVehicles = resp?.data?.data
      })
    }
  },
  computed: {
    timeRemaining() {
      let timeRemainingString = 'N/A'
      let chargeTimeRemaining = this.connectionDetails?.chargeTimeRemaining
      let realMinutes
      let hours

      if (this.connectionDetails?.fullyCharged) {
        timeRemainingString = this.$t('enode-connection-tile.isFullyCharged')
      } else if (Number.isInteger(chargeTimeRemaining)) {
        realMinutes = this.connectionDetails.chargeTimeRemaining % 60

        if (chargeTimeRemaining > 60) {
          hours = (chargeTimeRemaining - realMinutes) / 60
          timeRemainingString = hours + this.$t('app.unit.hours') + ' ' + realMinutes + this.$t('app.unit.minutes') + ' ' + this.$t('enode-connection-tile.remaining-time') + ' ' + this.connectionDetails.chargeLimit + this.$t(
              'app.unit.percent')
        } else if (chargeTimeRemaining < 60) {
          timeRemainingString = realMinutes + this.$t('app.unit.minutes') + ' ' + this.$t('enode-connection-tile.remaining-time') + ' ' + this.connectionDetails.chargeLimit + this.$t(
              'app.unit.percent')
        }
      }

      return timeRemainingString
    },
    progressVarBufferValue() {
      return this.connectionDetails.charging ? 0 : 100
    },
    title() {
      return this.connectionDetails?.brand + ' ' + this.connectionDetails?.model
    },
    progressBarColor() {
      let color = '#E0E0E0'

      if (this.connectionDetails.pluggedIn && this.connectionDetails.reachable) {
        color = '#FCB662'
        if (this.connectionDetails.batteryLevel >= 80) {
          color = '#39D9BA'
        } else if (this.connectionDetails.batteryLevel <= 20) {
          color = '#FC627A'
        }
      }

      return color
    },
    showSameMultipleVehicles() {
      return this.amountOfSameVehicles > 1
    },
    isSmartChargingPaused() {
      return this.connectionDetails.smartChargingState === SmartChargingStates.STOPPED ||
          this.connectionDetails.smartChargingState === SmartChargingStates.STOPPED_AWAITING_PRICES
    },
    isSmartChargingOverriden() {
      return this.connectionDetails.smartChargingState === SmartChargingStates.OVERRIDDEN
    },
    isChargeStateTransitioning() {
      // Check if regular charging is in a pending state
      const isRegularChargingTransitioning = !this.connectionDetails.hasSmartChargingPlan &&
                                             this.actionState === ChargeActionStates.PENDING &&
                                             (this.requestedAction === ChargeActions.STOP ||
                                              this.requestedAction === ChargeActions.START)

      // Check if smart charging is transitioning
      const isSmartChargingTransitioning = this.connectionDetails.smartChargingState === SmartChargingStates.STOPPING ||
                                           this.connectionDetails.smartChargingState === SmartChargingStates.STARTING

      // Check if smart charging is overridden but not currently charging
      const isSmartChargingOverridenNotCharging = this.isSmartChargingOverriden && !this.connectionDetails.charging

      return isRegularChargingTransitioning || isSmartChargingTransitioning || isSmartChargingOverridenNotCharging
    },
  },
  watch: {},
  mounted() {
    this.getAmountOfVehiclesWithSameVin()
  }
}
</script>

<style lang="scss">
@import '~@/styles/living/e-mobility.scss';
</style>
